import React, { useEffect, useRef, useState, useMemo } from 'react';
import { DeliveryOption, PrintSize } from '../../prop-types';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { allPhotosCollectedByRootImage } from '../../common/collect-by-root-image';
import {
  sizesWithPrice,
  sizesWithRootImage,
  totalQuantity,
} from '../../common/totalizing-mixin';
import Photo from '../photos-grid/photo';
import PhotoFilename from '../../common/photo-filename';
import findRecommendedPrintSize from '../../common/photo-quality/recommended-print-size';
import Paginator from '../../../_utils/paginator';
import { usePagination } from '../../../_utils/paginator/use-pagination';
import pluralize from '../../../_utils/tools/pluralize';
import PrintCountDisplay from '../print-count-display';
import Button from '../../../_common/button';
import SideMenu from './side-menu';
import ResizeOptionsModal from '../resize-options-modal';
import Footer from '../footer';
import { builderDispatch } from '../../../_utils/ui';
import { UpdatePhotoEvents } from '../../common/events/event-declarations';
import buildPhotoSizeBadges from '../../common/photo-quality/build-photo-size-badges';
import getPhotoPreviewWarningProps from '../../common/photo-quality/photo-preview-warning-props';

const modalType = {
  NONE: undefined,
  CHANGE_SIZE: 'change_size',
  ADD_SIZE: 'add_size',
};

export default function PhotosGrid({
  sizes,
  availableSizes,
  viewingSizeKey,
  onAdvancedEditPhoto,
  deliveryOption,
  orderedPhotos,
  invalidDeliveryOptionSelected,
  handleAddPhotos,
}) {
  const PHOTOS_PER_PAGE = 25;
  const { recommendedPrintSizes, photoBadgesMap } = useMemo(() => {
    const recSizes = findAllRecommendedPrintSizes();
    const badges = calculatePhotoBadges(
      orderedPhotos,
      recSizes,
      availableSizes,
    );
    return { recommendedPrintSizes: recSizes, photoBadgesMap: badges };
  }, [orderedPhotos, availableSizes]);

  const totalPhotosLength = totalQuantity(orderedPhotos);
  const {
    page,
    paginatedItems,
    totalPageCount,
    handlePageNext,
    handlePagePrevious,
    handleGoToPage,
  } = usePagination({
    items: orderedPhotos,
    itemsPerPage: PHOTOS_PER_PAGE,
  });
  const defaultSelectedPhoto = {
    raw_dimensions: { w: 1000, h: 1000 },
    root_image_id: undefined,
  };
  const selectedPhotoRef = useRef(defaultSelectedPhoto);
  const [selectedModal, setSelectedModal] = useState(modalType.NONE);
  const selectedPhoto = selectedPhotoRef.current;

  useEffect(() => {
    handleGoToPage(1);
  }, [viewingSizeKey]); // reset page if size filter changes

  function findAllRecommendedPrintSizes() {
    const photosCollection = allPhotosCollectedByRootImage(sizes);

    return Object.entries(photosCollection).reduce(
      (acc, [id, photoSizes = {}]) => {
        const photo = (Object.values(photoSizes)[0] || [])[0];
        acc[id] = findRecommendedPrintSize(availableSizes, photo);
        return acc;
      },
      {},
    );
  }

  function calculatePhotoBadges(
    allPhotos,
    recommendedPrintSizes,
    allAvailableSizes,
  ) {
    const badges = new Map(); // Map: rootId -> Map: sizeKey -> badgeType

    // Group all photo instances by root_image_id for easier processing
    const photosByRootId = new Map();
    const allPhotoInstances = allPhotos.flatMap((pg) => pg.photos);

    allPhotoInstances.forEach((p) => {
      if (!p) return;
      if (!photosByRootId.has(p.root_image_id)) {
        photosByRootId.set(p.root_image_id, []);
      }
      photosByRootId.get(p.root_image_id).push(p);
    });

    photosByRootId.forEach((photoInstances, rootId) => {
      const currentRecommendedSize = recommendedPrintSizes[String(rootId)];
      const rootBadges = buildPhotoSizeBadges(
        allAvailableSizes,
        photoInstances,
        currentRecommendedSize,
      );
      badges.set(String(rootId), rootBadges);
    });

    return badges;
  }

  function addSize(targetSizeKey) {
    builderDispatch.dispatch({
      type: UpdatePhotoEvents.ADD_TO_SIZE,
      rootImageId: selectedPhoto.root_image_id,
      targetSizeKey,
      quantity: 1,
    });
  }

  function handleCloseModal() {
    selectedPhotoRef.current = defaultSelectedPhoto;
    setSelectedModal(modalType.NONE);
  }

  const handleSizeChange = (targetSizeKey) => {
    builderDispatch.dispatch({
      type: UpdatePhotoEvents.RESIZE,
      clientUuid: selectedPhoto.client_uuid,
      targetSizeKey,
    });
  };

  const modalOptions = {
    [modalType.CHANGE_SIZE]: {
      title: 'Change Size',
      onClick: (targetSizeKey) => handleSizeChange(targetSizeKey),
    },
    [modalType.ADD_SIZE]: {
      title: 'Add Size',
      onClick: (targetSizeKey) => addSize(targetSizeKey),
    },
  };

  return (
    <>
      <div className="list-view list-view-mobile">
        {paginatedItems.map(({ id, photos = [], isLastPhoto }) => (
          <section
            key={`photo_${id}`}
            className={classNames('d-flex flex-column list-item', {
              last: isLastPhoto,
            })}>
            <PhotoFilename photo={photos[0]} />
            <Button
              className="btn btn-sm btn-link list-view-mobile__add-size add-size"
              type="button"
              text="Add Size"
              onClick={() => {
                selectedPhotoRef.current = photos[0];
                setSelectedModal(modalType.ADD_SIZE);
              }}
            />

            {photos.map((photo) => {
              const rootBadgesMap = photoBadgesMap.get(String(id));
              const currentRecommendedSize = recommendedPrintSizes[id];

              const { showPreviewWarning, recommendedPrintSizeTextForPreview } =
                getPhotoPreviewWarningProps(
                  photo,
                  rootBadgesMap,
                  currentRecommendedSize,
                );

              return (
                <div
                  key={`${id}_${photo.size.size_id}`}
                  className="list-item__size row">
                  <div className="col-6 list-item__size__photo">
                    {Photo(
                      photo,
                      isLastPhoto,
                      sizesWithRootImage(sizes, id),
                      availableSizes,
                      onAdvancedEditPhoto,
                      recommendedPrintSizes[id],
                      showPreviewWarning,
                      recommendedPrintSizeTextForPreview,
                    )}
                  </div>
                  <div className="col-6 list-item__size__side-menu">
                    <SideMenu
                      photo={photo}
                      handleResizeOptionsModal={() => {
                        selectedPhotoRef.current = photo;
                        setSelectedModal(modalType.CHANGE_SIZE);
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </section>
        ))}
        {selectedModal && (
          <ResizeOptionsModal
            title={modalOptions[selectedModal].title}
            sizesWithPrints={sizesWithRootImage(
              sizes,
              selectedPhoto.root_image_id,
            )}
            onClick={(targetSizeKey) => {
              modalOptions[selectedModal].onClick(targetSizeKey);
              handleCloseModal();
            }}
            showResizeOptionsModal={!!selectedModal}
            deliveryOption={deliveryOption}
            handleResizeOptionsModal={handleCloseModal}
            recommendedPrintSize={
              recommendedPrintSizes[selectedPhoto.root_image_id]
            }
            sizesWithBadges={sizesWithPrice(
              availableSizes,
              sizes,
              deliveryOption,
            ).map((s) => ({
              ...s,
              badgeType: photoBadgesMap
                .get(String(selectedPhoto.root_image_id))
                ?.get(s.key),
            }))}
          />
        )}
        <Footer>
          <Paginator
            title="Uploaded photos pages"
            page={page}
            totalPageCount={totalPageCount}
            scrollTarget={-200}
            onPageNext={handlePageNext}
            onPagePrevious={handlePagePrevious}
            onGoToPage={handleGoToPage}
          />
          <div className="w-100 d-flex align-items-center justify-content-between">
            <PrintCountDisplay
              hideTotal
              selectedCount={totalPhotosLength}
              totalCount={totalPhotosLength}
              text={pluralize('photo', totalPhotosLength)}
            />
            <Button
              className="btn btn-link btn-underline"
              onClick={handleAddPhotos}
              disabled={invalidDeliveryOptionSelected}>
              + Add more photos
            </Button>
          </div>
        </Footer>
      </div>
    </>
  );
}

PhotosGrid.propTypes = {
  deliveryOption: DeliveryOption,
  availableSizes: PropTypes.arrayOf(PrintSize),
  sizes: PropTypes.arrayOf(PrintSize),
  invalidDeliveryOptionSelected: PropTypes.bool,
}.isRequired;
